import { useQuery } from '@tanstack/react-query';
import { mePermissionsQueryOptions } from '@/lib/permissions-query';
import type { PermissionString } from '@vagusx/shared';

/**
 * Hook to get the current user's resolved permissions.
 * Calls GET /api/users/me/permissions — returns what tenant middleware already computed.
 * UX-only — backend enforces actual permissions.
 */
export function useCurrentPermissions() {
  const { data, isLoading } = useQuery(mePermissionsQueryOptions);

  return {
    permissions: data ? new Set(data.permissions) : new Set<string>(),
    scopes: data ? new Map(Object.entries(data.scopes)) : new Map<string, string>(),
    isSystemAdmin: data?.isSystemAdmin ?? false,
    isLoading,
  };
}

/**
 * Check if the current user has a specific permission.
 * UX-only — backend enforces actual permissions.
 */
export function useHasPermission(perm: PermissionString): boolean {
  const { permissions, isSystemAdmin, isLoading } = useCurrentPermissions();
  if (isLoading) return false;
  // System admins always have all permissions
  return isSystemAdmin || permissions.has(perm);
}

/**
 * Check if the current user is a system admin.
 */
export function useIsAdmin(): boolean {
  const { isSystemAdmin } = useCurrentPermissions();
  return isSystemAdmin;
}
